import { useCallback, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { getProductNameByProduct, Product } from "@/types/subscription";
import {
  useCurrentUser,
  useCurrentOrganization,
  usePortalReducers,
  generateApiKey,
} from "@/hooks/spacetime";
import { Check, Copy } from "lucide-react";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { Input } from "./ui/input";

interface ApiKeysCreateDialogProps {
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

/**
 * `org_api_keys` rows arrive with `apiKey` already masked
 * (`sk_...<last4>` — see mapApiKey/the org_api_keys view). The full,
 * unmasked key only ever exists here, client-side, between generation and
 * the "Save your key" screen closing — it is never re-derived from the view.
 */
interface GeneratedApiKey {
  apiKey: string;
  product: Product;
}

export default function ApiKeysCreateDialog({
  controlledOpen,
  onOpenChange,
  trigger,
}: ApiKeysCreateDialogProps) {
  const { user } = useCurrentUser();
  const { organization } = useCurrentOrganization();
  const { createApiKey } = usePortalReducers();
  const [generatedKey, setGeneratedKey] = useState<GeneratedApiKey | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [product, setProduct] = useState<Product>("poweron-pipelines");

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedKey?.apiKey || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const handleCreateApiKey = async () => {
    if (!organization || !user) return;

    try {
      setIsCreating(true);
      const apiKey = generateApiKey();
      await createApiKey({
        organizationId: organization.id,
        userId: user.id,
        product,
        apiKey,
      });
      setGeneratedKey({ apiKey, product });
    } catch (error: any) {
      console.warn(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        className="bg-card"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (generatedKey !== null) {
            event.preventDefault();
          }
        }}
      >
        {generatedKey === null ? (
          <>
            <DialogHeader className="flex">
              <DialogTitle className="text-gray-1100 dark:text-gray-100">
                Create new secret key
              </DialogTitle>
              <DialogDescription className="text-secondary">
                This API key is for your organization and will be used to
                authenticate requests to Libum APIs without user authentication.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              <Label htmlFor="product">Product</Label>
              <Select
                value={product}
                onValueChange={(val: Product) => setProduct(val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="poweron-pipelines">
                    PowerOn Pipelines
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="form">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateApiKey}>
                <SaveButtonSpinner
                  show={isCreating}
                  margin="right"
                  collapseGap
                  iconClassName="text-current"
                />
                Create secret key
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="flex">
              <DialogTitle className="text-gray-1100 dark:text-gray-100">
                Save your key
              </DialogTitle>
              <DialogDescription className="text-secondary">
                Please save your secret key in a safe place since{" "}
                <span className="font-bold">
                  you will not be able to view it again
                </span>
                . If you do lose it, you&apos;ll need to generate a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              <div className="relative w-full">
                <Input
                  id="sk"
                  className="mb-4 mt-0 pr-[79px] text-gray-1100 dark:text-gray-100"
                  placeholder="sk_...1234"
                  defaultValue={generatedKey.apiKey}
                  type="text"
                  readOnly
                />
                <Button
                  size="sm"
                  variant="form"
                  className="absolute right-1.5 top-1.5 gap-1 rounded-xs bg-emerald-400 text-white hover:bg-emerald-500"
                  onClick={handleCopy}
                >
                  {copied ? <Check /> : <Copy />}
                  Copy
                </Button>
              </div>

              <Label>Product</Label>
              <span className="text-sm text-secondary">
                {getProductNameByProduct(generatedKey.product)}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="form"
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => {
                    setGeneratedKey(null);
                    setIsCreating(false);
                  }, 100);
                }}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
